import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import DateRangePicker from "./DateRangePicker";
import { countryName } from "./countries";
import {
  API_BASE_URL,
  ChartTip,
  DeltaText,
  MONEY,
  PeriodSwitch,
  STAGE_LABELS,
  Sparkline,
  TYPE_META,
  TYPE_ORDER,
  dayLabel,
  delta,
  num,
  periodParams,
  periodPhrase,
  plural,
  rub,
  secs,
  TIME_STAGES,
  whenLabel,
} from "./analyticsShared";

import "./analytics.css";

// ru_* — документы РФ, как группирует бэкенд
const docGroupOf = (t) =>
  ["passport", "drivers_license", "contract"].includes(t)
    ? t
    : t && t.startsWith("ru_")
      ? "civil"
      : t
        ? "other"
        : "passport";

const axisTick = { fontSize: 12, fill: "#6B6D75" };

const Kpi = ({ label, value, sub, d, spark, color }) => (
  <div className="an-kpi">
    <span className="an-kpi-label">{label}</span>
    <span className="an-kpi-value">{value}</span>
    {sub && <span className="an-kpi-sub">{sub}</span>}
    <span className="an-kpi-foot">
      <DeltaText d={d} />
      <Sparkline values={spark} color={color} />
    </span>
  </div>
);

const buildAlerts = (data, pendingRequests) => {
  const out = [];
  const { summary, errors, clients } = data;
  if (summary.ai_cost > 0 && summary.free > 0) {
    const share = Math.round((summary.free_ai_cost / summary.ai_cost) * 100);
    if (share >= 50)
      out.push({
        tone: "warn",
        title: `Бесплатные документы — ${share}% затрат на AI`,
        text: `${num(summary.free)} ${plural(summary.free, "бесплатный документ стоил", "бесплатных документа стоили", "бесплатных документов стоили")} ${rub(summary.free_ai_cost)}.`,
        href: "#an-types",
      });
  }
  const low = clients.filter((c) => c.status === "low_balance");
  if (low.length)
    out.push({
      tone: "warn",
      title: `${low.length} ${plural(low.length, "компания", "компании", "компаний")}: баланса меньше чем на 3 дня`,
      text: low
        .slice(0, 3)
        .map((c) => `${c.name} — ${rub(c.balance)}`)
        .join(", "),
      href: "#an-clients",
    });
  if (errors.total >= 3 && errors.rate > errors.prev_rate * 1.5)
    out.push({
      tone: "warn",
      title: "Ошибок обработки стало больше",
      text: `${errors.total} за период (${String(errors.rate).replace(".", ",")}%), было ${String(errors.prev_rate).replace(".", ",")}%.${errors.top_reasons[0] ? ` Чаще всего: ${errors.top_reasons[0].reason.toLowerCase()}.` : ""}`,
      href: "#an-quality",
    });
  const sp = data.speed?.current;
  const spPrev = data.speed?.previous;
  if (
    sp && spPrev && sp.measured >= 10 && spPrev.measured >= 10 &&
    sp.p90_ms > spPrev.p90_ms * 1.3
  )
    out.push({
      tone: "warn",
      title: "Документы обрабатываются дольше",
      text: `90% документов — до ${secs(sp.p90_ms)}, было до ${secs(spPrev.p90_ms)}.`,
      href: "#an-quality",
    });
  clients
    .filter((c) => c.status === "quiet")
    .slice(0, 2)
    .forEach((c) =>
      out.push({
        tone: "soft",
        title: `${c.name} затих`,
        text: `Последний документ ${c.quiet_days} ${plural(c.quiet_days, "день", "дня", "дней")} назад, в прошлом периоде — ${c.prev_docs}.`,
        href: "#an-clients",
      }),
    );
  if (pendingRequests > 0)
    out.push({
      tone: "soft",
      title: `${pendingRequests} ${plural(pendingRequests, "новая заявка", "новые заявки", "новых заявок")} на доступ`,
      text: "Ждут одобрения на вкладке «Заявки».",
    });
  return out;
};

const runwayCell = (c) => {
  if (c.status === "quiet")
    return <span className="an-muted">Затих {c.quiet_days} дн.</span>;
  if (c.runway_days == null)
    return (
      <span className="an-muted">
        {c.free_left > 0 ? `Бесплатно: осталось ${c.free_left}` : "Без платных документов"}
      </span>
    );
  if (c.runway_days < 1) return <span className="res-badge is-warn">Пополнить</span>;
  const label = `≈ ${Math.round(c.runway_days)} ${plural(Math.round(c.runway_days), "день", "дня", "дней")}`;
  return c.status === "low_balance" ? (
    <span className="res-badge is-warn">{label}</span>
  ) : (
    <span className="an-good">{label}</span>
  );
};

const CLIENT_FILTERS = [
  { key: "all", label: "Все" },
  { key: "low_balance", label: "Заканчивается баланс", tone: "warn" },
  { key: "quiet", label: "Затихли" },
];

const AdminAnalytics = ({ companies, pendingRequests = 0 }) => {
  const [period, setPeriod] = useState({ days: 30 });
  const [compare, setCompare] = useState(true);
  const [companyId, setCompanyId] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [clientFilter, setClientFilter] = useState("all");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios
      .get(`${API_BASE_URL}/api/admin/analytics`, {
        params: { ...periodParams(period), ...(companyId ? { company_id: companyId } : {}) },
      })
      .then((res) => {
        if (cancelled) return;
        setData(res.data);
        setError(null);
      })
      .catch(() => !cancelled && setError("Не удалось загрузить аналитику"))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [period, companyId]);

  const alerts = useMemo(() => (data ? buildAlerts(data, pendingRequests) : []), [data, pendingRequests]);

  if (!data)
    return (
      <div className="px-empty" role={error ? "alert" : "status"}>
        {error || "Загружаем аналитику…"}
      </div>
    );

  const { summary: s, previous: p, daily, errors } = data;
  const cmp = (a, b, opts) => (compare ? delta(a, b, opts) : null);
  const phrase = periodPhrase(period, data);
  const daysSeries = daily.map((d) => ({
    ...d,
    docs: TYPE_ORDER.reduce((n, t) => n + (d[t] || 0), 0),
    profit: d.revenue - d.ai_cost,
  }));
  const clients =
    clientFilter === "all" ? data.clients : data.clients.filter((c) => c.status === clientFilter);
  const countOf = (k) => data.clients.filter((c) => c.status === k).length;
  const types = TYPE_ORDER.filter((t) => data.by_type.some((x) => x.type === t));
  const maxStage = Math.max(1, ...errors.by_stage.map((x) => x.count));
  const sp = data.speed.current;
  const spPrev = data.speed.previous;
  const timedTypes = data.by_type.filter((t) => t.median_ms != null);
  const maxTypeTime = Math.max(1, ...timedTypes.map((t) => t.median_ms));
  const maxCountry = Math.max(1, ...data.countries.map((x) => x.count));
  const periodLink = period.custom
    ? `?start=${period.custom.start}&end=${period.custom.end}`
    : `?days=${period.days}`;

  return (
    <div className={`an${loading ? " is-loading" : ""}`} aria-busy={loading}>
      <div className="an-toolbar">
        <PeriodSwitch period={period} onChange={setPeriod} />
        <div className="an-range">
          <DateRangePicker
            key={`${data.period.start}_${data.period.end}`}
            initialRange={{ start: data.period.start, end: data.period.end }}
            onChange={(r) => r?.start && r?.end && setPeriod({ custom: r })}
          />
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={compare}
          className="an-switch"
          onClick={() => setCompare((v) => !v)}
        >
          <span className="an-switch-track" aria-hidden="true">
            <span />
          </span>
          Сравнить с {phrase}
        </button>
        <label className="an-company">
          <span>Компания</span>
          <select value={companyId} onChange={(e) => setCompanyId(e.target.value)}>
            <option value="">Все компании</option>
            {companies.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </label>
        <span className="an-live">
          <span aria-hidden="true" />
          За последний час: {data.last_hour.docs}{" "}
          {plural(data.last_hour.docs, "документ", "документа", "документов")} · {rub(data.last_hour.revenue)}
        </span>
      </div>

      {error && (
        <p role="alert" className="px-empty">
          {error}
        </p>
      )}

      <section className="an-kpis" aria-label="Главные показатели">
        <Kpi
          label="Выручка"
          value={rub(s.revenue)}
          sub={`${num(s.paid)} ${plural(s.paid, "платный документ", "платных документа", "платных документов")}`}
          d={cmp(s.revenue, p.revenue)}
          spark={daysSeries.map((d) => d.revenue)}
          color={MONEY.revenue}
        />
        <Kpi
          label="Себестоимость AI"
          value={rub(s.ai_cost)}
          sub={`$${String(s.ai_cost_usd).replace(".", ",")} · ${rub(s.ai_per_doc, 2)} на документ`}
          d={cmp(s.ai_cost, p.ai_cost, { higherIsBetter: false })}
          spark={daysSeries.map((d) => d.ai_cost)}
          color={MONEY.ai}
        />
        <Kpi
          label="Прибыль"
          value={rub(s.profit)}
          sub={s.margin == null ? "Выручки не было" : `Маржа ${Math.round(s.margin)}%`}
          d={
            s.margin != null && p.margin != null
              ? cmp(s.margin, p.margin, { pp: true })
              : cmp(s.profit, p.profit)
          }
          spark={daysSeries.map((d) => Math.max(0, d.profit))}
          color={MONEY.revenue}
        />
        <Kpi
          label="Документы"
          value={num(s.docs)}
          sub={`${num(s.paid)} платных · ${num(s.free)} бесплатных`}
          d={cmp(s.docs, p.docs)}
          spark={daysSeries.map((d) => d.docs)}
          color="#5B7FB8"
        />
        <Kpi
          label="Активные компании"
          value={num(s.active_companies)}
          sub={`из ${num(data.total_companies)} с доступом`}
          d={cmp(s.active_companies, p.active_companies)}
        />
      </section>

      <div className="an-row">
        <section className="an-card an-grow-2" aria-labelledby="an-money">
          <div className="an-card-head">
            <div>
              <h2 id="an-money" className="an-title">Деньги по дням</h2>
              <p className="an-sub">Выручка и себестоимость AI, ₽</p>
            </div>
            <span className="an-legend">
              <span><i style={{ background: MONEY.revenue }} />Выручка</span>
              <span><i style={{ background: MONEY.ai }} />Себестоимость AI</span>
            </span>
          </div>
          <div className="an-chart" role="img" aria-label={`Выручка ${rub(s.revenue)} и себестоимость AI ${rub(s.ai_cost)} по дням`}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={daysSeries} barGap={2} margin={{ top: 8, right: 0, bottom: 0, left: 0 }}>
                <CartesianGrid vertical={false} stroke="#F0F1F3" />
                <XAxis dataKey="date" tickFormatter={dayLabel} tick={axisTick} axisLine={false} tickLine={false} minTickGap={24} />
                <YAxis tick={axisTick} axisLine={false} tickLine={false} width={48} tickFormatter={(v) => num(v)} />
                <Tooltip
                  cursor={{ fill: "rgba(22,23,27,0.04)" }}
                  content={<ChartTip rows={(d) => [["Выручка", rub(d.revenue)], ["Себестоимость AI", rub(d.ai_cost, 2)], ["Итог", rub(d.profit, 2)]]} />}
                />
                <Bar isAnimationActive={false} dataKey="revenue" fill={MONEY.revenue} radius={[3, 3, 0, 0]} maxBarSize={10} />
                <Bar isAnimationActive={false} dataKey="ai_cost" fill={MONEY.ai} radius={[3, 3, 0, 0]} maxBarSize={10} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </section>

        <section className="an-attn an-grow-1" aria-labelledby="an-attn">
          <h2 id="an-attn" className="an-title">Требует внимания</h2>
          {alerts.length === 0 ? (
            <p className="an-attn-calm">Всё спокойно: балансы в порядке, ошибок не больше обычного.</p>
          ) : (
            alerts.map((a) => {
              const body = (
                <>
                  <span className={`an-dot is-${a.tone}`} aria-hidden="true" />
                  <span>
                    <span className="an-attn-title">{a.title}</span>
                    <span className="an-attn-text">{a.text}</span>
                  </span>
                </>
              );
              return a.href ? (
                <a key={a.title} href={a.href} className="an-attn-item">
                  {body}
                </a>
              ) : (
                <div key={a.title} className="an-attn-item">
                  {body}
                </div>
              );
            })
          )}
        </section>
      </div>

      <div className="an-row">
        <section className="an-card an-grow-1" aria-labelledby="an-docs">
          <h2 id="an-docs" className="an-title">Документы по дням</h2>
          <p className="an-sub">По типам, штук</p>
          <span className="an-legend an-legend-block">
            {types.map((t) => (
              <span key={t}>
                <i style={{ background: TYPE_META[t].color }} />
                {TYPE_META[t].label}
              </span>
            ))}
          </span>
          <div className="an-chart is-short" role="img" aria-label={`Документы по дням, всего ${num(s.docs)}`}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={daysSeries} margin={{ top: 8, right: 0, bottom: 0, left: 0 }}>
                <CartesianGrid vertical={false} stroke="#F0F1F3" />
                <XAxis dataKey="date" tickFormatter={dayLabel} tick={axisTick} axisLine={false} tickLine={false} minTickGap={24} />
                <YAxis tick={axisTick} axisLine={false} tickLine={false} width={32} allowDecimals={false} />
                <Tooltip
                  cursor={{ fill: "rgba(22,23,27,0.04)" }}
                  content={<ChartTip rows={(d) => [...types.filter((t) => d[t]).map((t) => [TYPE_META[t].label, num(d[t])]), ["Всего", num(d.docs)]]} />}
                />
                {types.map((t, i) => (
                  <Bar
                    isAnimationActive={false}
                    key={t}
                    dataKey={t}
                    stackId="docs"
                    fill={TYPE_META[t].color}
                    stroke="#FFFFFF"
                    strokeWidth={1}
                    maxBarSize={18}
                    radius={i === types.length - 1 ? [3, 3, 0, 0] : 0}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>
        </section>

        <section className="an-card an-grow-1" aria-labelledby="an-types">
          <h2 id="an-types" className="an-title">Экономика по типам документов</h2>
          <p className="an-sub">Сколько приносит и сколько стоит каждый тип</p>
          <div className="px-table-wrap">
            <table className="an-table" style={{ minWidth: 440 }}>
              <thead>
                <tr>
                  <th scope="col">Тип</th>
                  <th scope="col" className="num">Док.</th>
                  <th scope="col" className="num">Выручка</th>
                  <th scope="col" className="num">AI на док.</th>
                  <th scope="col">Результат</th>
                </tr>
              </thead>
              <tbody>
                {data.by_type.map((t) => (
                  <tr key={t.type}>
                    <td>
                      <span className="an-type">
                        <i style={{ background: (TYPE_META[t.type] || TYPE_META.other).color }} aria-hidden="true" />
                        {(TYPE_META[t.type] || TYPE_META.other).label}
                      </span>
                    </td>
                    <td className="num">{num(t.docs)}</td>
                    <td className="num">{rub(t.revenue)}</td>
                    <td className="num an-muted">{rub(t.ai_per_doc, 2)}</td>
                    <td className={t.profit >= 0 ? "an-good" : "an-bad"}>
                      {t.profit >= 0 ? "+" : "−"}
                      {rub(Math.abs(t.profit))}
                      {t.revenue > 0
                        ? ` · маржа ${Math.round((t.profit / t.revenue) * 100)}%`
                        : " · бесплатно"}
                    </td>
                  </tr>
                ))}
                {data.by_type.length === 0 && (
                  <tr>
                    <td colSpan="5" className="an-muted">Документов за период нет</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {s.free > 0 && s.ai_cost > 0 && (
            <p className="an-note">
              Бесплатные документы: {num(s.free)} шт., себестоимость {rub(s.free_ai_cost)} —{" "}
              {Math.round((s.free_ai_cost / s.ai_cost) * 100)}% всех затрат на AI.
            </p>
          )}
        </section>
      </div>

      <section className="an-card" aria-labelledby="an-clients">
        <div className="an-card-head">
          <div>
            <h2 id="an-clients" className="an-title">Клиенты</h2>
            <p className="an-sub">
              {num(s.active_companies)} {plural(s.active_companies, "активная", "активные", "активных")} за период · по выручке
            </p>
          </div>
          <div className="an-pills" role="radiogroup" aria-label="Показать клиентов">
            {CLIENT_FILTERS.map((f) => {
              const n = f.key === "all" ? data.clients.length : countOf(f.key);
              const active = clientFilter === f.key;
              return (
                <button
                  key={f.key}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  className={`an-pill${active ? " is-active" : ""}${f.tone && n > 0 && !active ? " is-warn" : ""}`}
                  onClick={() => setClientFilter(f.key)}
                >
                  {f.label} · {n}
                </button>
              );
            })}
          </div>
        </div>
        <div className="px-table-wrap">
          <table className="an-table" style={{ minWidth: 960 }}>
            <thead>
              <tr>
                <th scope="col">Компания</th>
                <th scope="col">Документы</th>
                <th scope="col" className="num">Выручка</th>
                <th scope="col" className="num">Себестоимость</th>
                <th scope="col" className="num">Прибыль</th>
                <th scope="col" className="num">Баланс</th>
                <th scope="col">Хватит на</th>
                <th scope="col">Последний документ</th>
              </tr>
            </thead>
            <tbody>
              {clients.map((c) => (
                <tr key={c.id}>
                  <td>
                    <Link to={`/admin/companies/${c.id}${periodLink}`} className="an-company-link">
                      {c.name}
                    </Link>
                    <span className="an-cell-sub">
                      {c.users} {plural(c.users, "сотрудник", "сотрудника", "сотрудников")}
                    </span>
                  </td>
                  <td>
                    <span className="an-spark-cell">
                      <Sparkline values={c.spark} width={88} height={24} />
                      <strong>{num(c.docs)}</strong>
                    </span>
                  </td>
                  <td className="num"><strong>{rub(c.revenue)}</strong></td>
                  <td className="num an-muted">{rub(c.ai_cost)}</td>
                  <td className={`num ${c.profit >= 0 ? "an-good" : "an-bad"}`}>
                    {c.profit >= 0 ? "+" : "−"}
                    {rub(Math.abs(c.profit))}
                  </td>
                  <td className="num">{rub(c.balance)}</td>
                  <td>{runwayCell(c)}</td>
                  <td className="an-muted">{whenLabel(c.last_doc_at)}</td>
                </tr>
              ))}
              {clients.length === 0 && (
                <tr>
                  <td colSpan="8" className="an-muted">Таких клиентов нет</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <div className="an-row">
        <section className="an-card an-grow-1" aria-labelledby="an-quality">
          <h2 id="an-quality" className="an-title">Скорость и качество</h2>
          <p className="an-sub">Сколько ждут результат и сколько документов падает с ошибкой</p>

          <div className="an-stats">
            <div>
              <span className="an-stat-label">Обычно</span>
              <strong>{secs(sp.median_ms)}</strong>
              <span className="an-muted">медиана</span>
            </div>
            <div>
              <span className="an-stat-label">90% документов</span>
              <strong>{sp.p90_ms == null ? "—" : `до ${secs(sp.p90_ms)}`}</strong>
              {compare && sp.p90_ms != null && spPrev.p90_ms != null ? (
                <DeltaText d={delta(sp.p90_ms, spPrev.p90_ms, { higherIsBetter: false })} />
              ) : (
                <span className="an-muted">90-й перцентиль</span>
              )}
            </div>
            <div>
              <span className="an-stat-label">Ошибки</span>
              <strong>{String(errors.rate).replace(".", ",")}%</strong>
              <span className={errors.rate > errors.prev_rate ? "an-bad" : "an-muted"}>
                {compare ? `было ${String(errors.prev_rate).replace(".", ",")}% · ` : ""}
                {num(errors.total)} {plural(errors.total, "ошибка", "ошибки", "ошибок")}
              </span>
            </div>
          </div>

          {sp.measured === 0 ? (
            <p className="an-note is-soft">
              Время обработки собираем — цифры появятся после первых документов,
              обработанных после обновления.
            </p>
          ) : (
            <>
              {sp.measured < s.docs && (
                <p className="an-muted an-small">
                  Замер есть у {num(sp.measured)} из {num(s.docs)} документов — у более
                  ранних время не записывалось.
                </p>
              )}
              <h3 className="an-h3">Время по типам документов</h3>
              {timedTypes.map((t) => (
                <div key={t.type} className="an-barrow">
                  <span>{(TYPE_META[t.type] || TYPE_META.other).label}</span>
                  <span className="an-bar">
                    <span style={{ width: `${(t.median_ms / maxTypeTime) * 100}%`, background: (TYPE_META[t.type] || TYPE_META.other).color }} />
                  </span>
                  <strong className="an-wide">{secs(t.median_ms)}</strong>
                </div>
              ))}

              {sp.stages.length > 0 && (
                <>
                  <h3 className="an-h3">На что уходит время</h3>
                  <div className="an-stack" role="img" aria-label={sp.stages.map((x) => `${(TIME_STAGES[x.stage] || {}).label || x.stage} ${Math.round(x.share)}%`).join(", ")}>
                    {sp.stages.map((x) => (
                      <span key={x.stage} style={{ width: `${x.share}%`, background: (TIME_STAGES[x.stage] || {}).color || "#8A8C94" }} />
                    ))}
                  </div>
                  <ul className="an-stack-legend">
                    {sp.stages.map((x) => (
                      <li key={x.stage}>
                        <i style={{ background: (TIME_STAGES[x.stage] || {}).color || "#8A8C94" }} aria-hidden="true" />
                        {(TIME_STAGES[x.stage] || {}).label || x.stage}
                        <span className="an-muted">
                          {secs(x.avg_ms)} · {Math.round(x.share)}%
                        </span>
                      </li>
                    ))}
                  </ul>
                </>
              )}

              {data.speed.slowest.length > 0 && (
                <>
                  <h3 className="an-h3">Самые долгие</h3>
                  <ul className="an-reasons">
                    {data.speed.slowest.map((d, i) => (
                      <li key={`${d.record_id}-${i}`}>
                        <span>
                          {(TYPE_META[docGroupOf(d.doc_type)] || TYPE_META.other).label}
                          {d.company && (
                            <>
                              {" · "}
                              <Link to={`/admin/companies/${d.company_id}${periodLink}`} className="an-company-link">
                                {d.company}
                              </Link>
                            </>
                          )}
                          <span className="an-muted"> · {whenLabel(d.created_at)}</span>
                        </span>
                        <strong>{secs(d.duration_ms)}</strong>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </>
          )}

          {errors.total > 0 && (
            <>
              <h3 className="an-h3">
                Ошибки по этапам
                {errors.median_wait_ms != null && (
                  <span className="an-muted"> · до ошибки ждали ~{secs(errors.median_wait_ms)}</span>
                )}
              </h3>
              {errors.by_stage.map((x) => (
                <div key={x.stage} className="an-barrow">
                  <span>{STAGE_LABELS[x.stage] || x.stage}</span>
                  <span className="an-bar"><span style={{ width: `${(x.count / maxStage) * 100}%`, background: MONEY.ai }} /></span>
                  <strong>{x.count}</strong>
                </div>
              ))}
              <h3 className="an-h3">Частые причины</h3>
              <ul className="an-reasons">
                {errors.top_reasons.map((r) => (
                  <li key={r.reason}>
                    <span>{r.reason}</span>
                    <strong>{r.count}</strong>
                  </li>
                ))}
              </ul>
            </>
          )}
        </section>

        <section className="an-card an-grow-1" aria-labelledby="an-origin">
          <h2 id="an-origin" className="an-title">Откуда документы</h2>
          <p className="an-sub">Страны выдачи и направления перевода</p>
          <h3 className="an-h3">Страны</h3>
          {data.countries.length === 0 && <p className="an-muted">Нет данных</p>}
          {data.countries.map((c) => (
            <div key={c.key} className="an-barrow">
              <span>{countryName(c.key)}</span>
              <span className="an-bar"><span style={{ width: `${(c.count / maxCountry) * 100}%`, background: "#5B7FB8" }} /></span>
              <strong>{num(c.count)}</strong>
            </div>
          ))}
          {data.language_pairs.length > 0 && (
            <>
              <h3 className="an-h3">Направления перевода</h3>
              <div className="an-chips">
                {data.language_pairs.map((l) => (
                  <span key={l.key}>
                    {l.key} <em>{num(l.count)}</em>
                  </span>
                ))}
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
};

export default AdminAnalytics;
